define('lq/events/eventing',[
    'underscore',
    'lq/events/actions/eventAction',
    'lq/events/actions/thirdPartyLogin',
    'lq/events/actions/federatedLogin',
    'lq/events/processEventActionsUrl',
    'when',
    'lq/validatorGateway'
], function (_, EventAction, ThirdPartyLogin, FederatedLogin, processEventActionsUrl, when, validatorGateway) {
    'use strict';

    var eventCounter = 0;

    var nextEventId = function() {
        return ++eventCounter;
    };

    var getFullEventName = function (event) {
        return event.eventSourceDropId
            ? (event.eventSourceDropId + '.' + event.eventName)
            : event.eventName;
    };

    var Eventing = function (events, piping, eventActionFactory, isReloaded) {
        this.events = events;
        this.piping = piping;
        this.eventActionFactory = eventActionFactory;
        this.subcriptions = {};
        this.beforeInterceptors = [];

        this.initialEventNames = isReloaded
            ? this.events.reloadedEventNames
            : this.events.loadedEventNames;
    };

    Eventing.prototype.onPageLoad = function (callback) {
        if (this.initialEventNames.length === 0) return;
        this.subscribe(this.initialEventNames[0], function(eventSource, eventArg, eventCompleted) {
            callback(eventCompleted);
        });
    };

    Eventing.prototype.subscribe = function(eventName, callback) {
        if (!this.subcriptions[eventName])
            this.subcriptions[eventName] = [];
        this.subcriptions[eventName].push(callback);
    };

    Eventing.prototype.addBeforeInterceptor = function (callback) {
        this.beforeInterceptors.push(callback);
    };

    Eventing.prototype.removeInterceptor = function (callback) {
        var index = this.beforeInterceptors.indexOf(callback);
        if (index >= 0)
            this.beforeInterceptors.splice(index, 1);
    };

    Eventing.prototype.hasSubscribers = function (eventName) {
        return this.subcriptions[eventName] !== undefined && this.subcriptions[eventName].length > 0;
    };

    Eventing.prototype.callBeforeInterceptors = function (eventName, args, parentEventAction) {
        var interceptedEventObj = {
            eventName: eventName,
            parentEventAction: parentEventAction,
            args: [args]
        };

        return when.all(_.map(this.beforeInterceptors, function (interceptor) {
            return interceptor(interceptedEventObj);
        }));
    };

    Eventing.prototype.publish = function (eventName, eventSource, args, callback, parentEventAction, abortion) {
        abortion = abortion ?? new AbortController();
        
        var subscriptions = this.subcriptions[eventName];

        var self = this;

        var interceptedEventObj = {
            eventName: eventName,
            parentEventAction: parentEventAction,
            args: [args]
        };

        when
            .all(_.map(this.beforeInterceptors, function (interceptor) {
                return interceptor(interceptedEventObj);
            }))
            .then(function() {
                return when
                    .all(_.map(subscriptions, function (subscription) {
                        if (abortion.signal.aborted) return;
                        
                        var deferred = when.defer();

                        subscription.apply(subscription, [
                            eventSource,
                            args, function () {
                                deferred.resolve();
                            },
                            parentEventAction,
                            abortion]);

                        return deferred.promise;
                    }))
            })
            .done(function () {
                if (typeof callback === 'function') {
                    callback();
                }
            });

        // notify parent about triggered event, but don't send it to just any domain
        if (window.parent) {
            var eventObj = {
                eventName: eventName,
                args: [args] // staying backwards compatible with existing custom code
            };

            var targetOrigin = self.piping.embeddable
                ? '*'
                : window.location.protocol + '//' + window.location.host;
            window.parent.postMessage(JSON.stringify(eventObj), targetOrigin);
        }
        
        if(!parentEventAction)
            return abortion;
    };

    Eventing.prototype.bindEvents = function () {
        var self = this;

        if (self.events.handlers.length === 0) {
            return;
        }
        var eventGroups = _.groupBy(self.events.handlers, function (event) {
            return getFullEventName(event);
        });
        _.each(eventGroups, function (eventGroup) {
            self.bindEventGroup(eventGroup, self.piping);
        });
    };

    Eventing.prototype.triggerPageLoadEvent = function () {
        var self = this;

        return this.initialEventNames.reduce(
            function (p, eventName) {
                return p.then(function (aborted) {
                    return new Promise(function(resolve) {
                        var handle = self.publish(eventName, null, null, function() {
                            // a regular break should not cause the page load to abort
                            resolve(aborted || (handle.signal.aborted && handle.signal.reason !== 'break'));
                        });
                    });
                });
            }, Promise.resolve(false))
            .then(function (aborted) {
                ThirdPartyLogin.afterPageLoad(self);
                FederatedLogin.afterPageLoad(self);
                return aborted;
            });
    };

    Eventing.prototype.bindEventGroup = function (eventGroup, piping) {
        if (eventGroup.length === 0)
            return;

        var conditionEvaluators = _.chain(eventGroup)
            .filter(function(event) {
                return !!event.conditionId;
            })
            .uniq(function (event) {
                return event.conditionId;
            })
            .map(function ( event) {
                return getConditionEvaluator(event, piping);
            })
            .value();

        var self = this;
        this.subscribe(
            getFullEventName(eventGroup[0]),
            function (eventSource, eventArg, eventCompleted, parentEventAction, abortion) {
                if (!(eventSource && eventSource.isBackgroundEvent)) {
                    piping.busyIndicator.show();
                }

                _.each(conditionEvaluators, function (conditionEvaluator) {
                    conditionEvaluator.clearResult();
                });

                when
                    .map(eventGroup, function (event) {
                        if (!event.conditionId) {
                            return {
                                event: event,
                                shouldExecute: true
                            };
                        }

                        var currentConditionEvaluator = _.findWhere(conditionEvaluators, { conditionId:  event.conditionId });

                        var d = currentConditionEvaluator.evaluate(piping, abortion.signal, eventSource && eventSource.isBackgroundEvent);

                        var promise = d.then(function (conditionData) {
                            if(abortion.signal.aborted) {
                                eventCompleted();
                            } else {
                                return {
                                    event: event,
                                    condition: conditionData,
                                    shouldExecute: event.invertCondition
                                        ? !conditionData.conditionResult
                                        : conditionData.conditionResult
                                };                                
                            }
                        });

                        return promise;
                    })
                    .done(function(eventsData) {
                        if(!self.eventActionFactory.rootDrop.isActive() || abortion.signal.aborted) return;

                        var afterChainCompleted = function () {
                            if (!(eventSource && eventSource.isBackgroundEvent)) {
                                piping.busyIndicator.hide();
                            }

                            if (typeof eventCompleted === 'function')
                                eventCompleted();
                        };

                        var executeNextEvent = function () {
                            if(!self.eventActionFactory.rootDrop.isActive()) return;
                            
                            if (eventsData.length === 0 || abortion.signal.aborted) {
                                afterChainCompleted();
                            } else {
                                var data = eventsData.splice(0, 1)[0];
                                var eventId = nextEventId();

                                var enterEvent = function () {
                                    if(validatorGateway.isEnabled()) {
                                        validatorGateway.publishEvent({
                                            type: 'ENTERING_EVENT',
                                            status: data.shouldExecute ? 'EXECUTING' : 'SKIPPING',
                                            actionInstanceId: self.eventActionFactory.rootDrop.instanceId,
                                            id: eventId,
                                            name: data.event.eventName,
                                            condition: data.condition ? {
                                                name: data.condition.condition.conditionName,
                                                expression: data.condition.condition.expression,
                                                inverted: data.event.invertCondition,
                                                values: data.condition.inputValues
                                            } : undefined,
                                            parentEventAction: parentEventAction,
                                            locals: piping.parameters.locals.getAll(),
                                            globals: piping.parameters.globals.getAll(),
                                            systems: piping.parameters.systems.getAll(),
                                            sourceDrop: eventSource ? eventSource.drop : undefined,
                                            isCanceled: data.isCanceled
                                        });
                                    }

                                    if(data.shouldExecute) {
                                        createAndExecuteEventActions(eventSource, data.event, eventId, eventArg, executeNextEvent, piping, abortion, self);
                                    } else {
                                        executeNextEvent();
                                    }
                                }
                                if (_.some(data.event.actions, function (a) {return !!a.mockMailTo
                                    || (a.redirect && a.redirect.mockJumpOptions && a.redirect.mockJumpOptions.length > 1);}) && !window.validator)  {
                                    validatorGateway.loadValidator({openedForPopups: true});
                                    validatorGateway.whenEnabled(enterEvent);
                                } else enterEvent();
                            }
                        };
                        
                        executeNextEvent();
                    });
            });
    };

    var getConditionEvaluator = function (event, piping) {
        return piping.conditions.getConditionEvaluator(event.conditionId);
    };

    var createAndExecuteEventActions = function (eventSource, event, eventId, eventArg, eventCompleted, piping, abortion, eventing) {
        if (event.conditionId) {
            var conditionEvaluator = getConditionEvaluator(event, piping);
            var inverse = "";
            if (event.invertCondition) {
                inverse = "NOT ";
            }
            startLogGroup("EVENT: " + event.eventName + " (" + inverse + conditionEvaluator.conditionName + ")");
        } else {
            startLogGroup("EVENT: " + event.eventName);
        }

        var eventActions = eventing.eventActionFactory.createEventActions(event, eventSource, eventArg, abortion);

        if(validatorGateway.isEnabled()) {
            _.each(eventActions, function (eventAction) {
                validatorGateway.publishEvent({
                    type: 'CREATED_EVENT_ACTION',
                    actionInstanceId: eventAction.rootDrop.instanceId,
                    id: eventAction.eventActionId,
                    eventId: eventId,
                    eventActionData: eventAction.eventData
                });
            });
        }

        executeEventActionBatches(event, eventSource, eventId, createEventActionBatches(eventActions), eventCompleted, piping, eventing, abortion);
    };

    var createEventActionBatches = function (eventActions) {
        if (eventActions.length === 0) return [];

        var batches = [];
        var currentBatch = [];
        var currentBatchType;
        batches.push(currentBatch);

        _.forEach(eventActions, function (eventAction) {
            var batchType = eventAction.getBatchType();
            if ((currentBatchType !== undefined && batchType !== currentBatchType) || batchType === EventAction.DO_NOT_BATCH) {
                if (currentBatch.length > 0) { //no need to start a new batch when the current batch is empty
                    currentBatch = [];
                    batches.push(currentBatch);
                }
            }
            currentBatch.push(eventAction);
            currentBatchType = batchType;
        });

        return batches;
    };

    var executeEventActionBatches = function (event, eventSource, eventId, eventActionBatches, eventCompleted, piping, eventing, abortion) {
        if(!eventing.eventActionFactory.rootDrop.isActive()) return;

        if (eventActionBatches.length === 0) { //all batches have been executed, event execution is finished completely
            endLogGroup();
            if (typeof eventCompleted === "function") {
                eventCompleted();
            }
            return;
        }

        var batch = eventActionBatches[0];
        eventActionBatches.splice(0, 1); //removes first element from array

        startLogGroup("<BATCH>");

        var executeNextBatch = function () {
            endLogGroup();
            executeEventActionBatches(event, eventSource, eventId, eventActionBatches, eventCompleted, piping, eventing, abortion);
        };

        createEventActionRequests(piping, eventId, batch, function (eventActionRequests) {
            if (eventActionRequests.length === 0) {
                executeBatch(eventId, batch, executeNextBatch, [], eventCompleted, abortion);
            } else {
                var parameters = piping.parameters;

                var dispatcher = eventSource && eventSource.isBackgroundEvent
                    ? piping.dispatcher.backgroundDispatch
                    : piping.dispatcher.dispatch;
                dispatcher = dispatcher.bind(piping.dispatcher);

                dispatcher(processEventActionsUrl.get(piping, 'Events'),
                    {
                        actionId: piping.actionId,
                        globalParameters: parameters.globals.getAll(),
                        localParameters: parameters.locals.getAll(),
                        requests: eventActionRequests
                    },
                    function (response, textStatus) {
                        if (abortion.signal.aborted) {
                            finishupEventAction(batch[0], null, eventCompleted, abortion);
                        } else {
                            if (!eventing.eventActionFactory.rootDrop.isActive()) return;

                            if (response.abort) {
                                piping.busyIndicator.forceHide();

                                if (response.eventName)
                                    piping.eventing.publish(response.eventName);
                                else
                                    alert(response.reason);
                            } else {
                                _.each(response.globalParameters, function (val, key) {
                                    parameters.globals.setValue(key, val);
                                });

                                _.each(response.localParameters, function (val, key) {
                                    parameters.locals.setValue(key, val);
                                });

                                executeBatch(eventId, batch, executeNextBatch, response.responses, eventCompleted, abortion);
                            }
                        }
                    },                        
                    {
                        signal: abortion.signal
                    },
                    function () {
                        if (typeof eventCompleted === "function")
                            eventCompleted();
                    }
                )
                .catch(function(xhr) {
                    if (xhr.statusText === 'abort') {
                        finishupEventAction(batch[0], null, eventCompleted, abortion);
                    }
                });
            }
        }, [], eventCompleted, abortion);
    };

    var createEventActionRequests = function (piping, eventId, batch, continuation, eventActionRequests, eventCompleted, abortion) {
        eventActionRequests = eventActionRequests || [];

        if (batch.length === 0) { //all requests have been created, continue with executing batch
            continuation(eventActionRequests);
            return;
        }

        var eventAction = batch[0];

        batch = batch.slice(0); //copies array (we cannot "clear" actual batch queue yet, because we still need to execute it)
        batch.splice(0, 1); //removes first element from array

        var processEventActionRequest = function (request) {
            if (abortion.signal.aborted) {
                piping.busyIndicator.forceHide();
                finishupEventAction(eventAction, null, eventCompleted, abortion);
            } else {
                if (request) {
                    request.eventActionId = eventAction.eventActionId;
                    eventActionRequests.push(request);
                }

                createEventActionRequests(piping, eventId, batch, continuation, eventActionRequests, eventCompleted, abortion);
            }
        };

        if(validatorGateway.isEnabled()) {
            validatorGateway.publishEvent({
                type: 'EXECUTING_EVENT_ACTION',
                actionInstanceId: eventAction.rootDrop.instanceId,
                id: eventAction.eventActionId,
                eventId: eventId,
                eventActionTypeName: eventAction.eventActionTypeName,
                locals: eventAction.piping.parameters.locals.getAll(),
                globals: eventAction.piping.parameters.globals.getAll(),
                systems: eventAction.piping.parameters.systems.getAll()
            });
        }

        var eventActionRequest = eventAction.createEventActionRequest();

        if (typeof eventActionRequest == "function") {
            eventActionRequest(function (request) {
                processEventActionRequest(request);
            }, function() {
                processEventActionRequest(null);
            });
        } else {
            processEventActionRequest(eventActionRequest);
        }
    };

    var executeBatch = function (eventId, batch, continuation, responses, eventCompleted, abortion) {
        if (batch.length === 0) { //batch is fully executed, move on to next batch
            continuation();
            return;
        }

        var eventAction = batch[0];
        batch.splice(0, 1); //removes first element from array

        var eventActionResponse = _.find(responses, function (response) {
            return response.eventActionId === eventAction.eventActionId;
        });

        if(validatorGateway.isEnabled() && eventActionResponse) {
            validatorGateway.publishEvent({
                type: 'HANDLING_EVENT_ACTION_RESPONSE',
                actionInstanceId: eventAction.rootDrop.instanceId,
                id: eventAction.eventActionId,
                eventId: eventId,
                eventActionTypeName: eventAction.eventActionTypeName,
                location: eventActionResponse ? 'SERVER' : 'CLIENT',
                locals: eventActionResponse.d
                    ? eventActionResponse.d.beforeParameters.locals
                    : eventAction.piping.parameters.locals.getAll(),
                globals: eventActionResponse.d
                    ? eventActionResponse.d.beforeParameters.globals
                    : eventAction.piping.parameters.globals.getAll(),
                systems: eventActionResponse.d
                    ? eventActionResponse.d.systems
                    : eventAction.piping.parameters.systems.getAll()
            });
        }

        startLogGroup(eventAction.eventActionId + " - " + eventAction.eventActionTypeName);
        log("Executing '" + eventAction.eventActionId + " - " + eventAction.eventActionTypeName + "'");

        eventAction.execute(
            function (options) {
                if(options && options.break)
                    abortion.abort(typeof options.break === 'string' ? options.break : 'break');
                
                finishupEventAction(eventAction, eventActionResponse, eventCompleted, abortion);
                
                if (abortion.signal.aborted) return;
                
                log("Finished executing '" + eventAction.eventActionId + " - " + eventAction.eventActionTypeName + "'");
                endLogGroup();
                executeBatch(eventId, batch, continuation, responses, undefined, abortion);
            }, //continue callback
            eventActionResponse
        );
    };

    var finishupEventAction = function (eventAction, eventActionResponse, eventCompleted, abortion) {
        if (validatorGateway.isEnabled()) {
            validatorGateway.publishEvent({
                type: abortion.signal.aborted && abortion.signal.reason !== 'break' ? 'CANCELED_EVENT_ACTION' : 'EXECUTED_EVENT_ACTION',
                actionInstanceId: eventAction.rootDrop.instanceId,
                id: eventAction.eventActionId,
                locals: eventActionResponse && eventActionResponse.d
                    ? eventActionResponse.d.afterParameters.locals
                    : eventAction.piping.parameters.locals.getAll(),
                globals: eventActionResponse && eventActionResponse.d
                    ? eventActionResponse.d.afterParameters.globals
                    : eventAction.piping.parameters.globals.getAll()
            });
        }

        if (abortion.signal.aborted) {
            log("Finished executing '" + eventAction.eventActionId + " - " + eventAction.eventActionTypeName + "'");
            while (logIndentationLevel > 0)
                endLogGroup();
            if (typeof eventCompleted === "function")
                eventCompleted();
        }
    };

    // **************************************************************
    // maybe we should disable log statements by default, because apparently it has some performance impact,
    // it should only be done when validating the page or something like that or with a special argument, or when debugging tools are open, or ...?
    // **************************************************************

    var logIndentationLevel = 0;
    var log = function (message) {
        if (window.console && window.console.log) {
            var logPrefix = '';
            if (typeof console.group === "undefined") {
                for (var i = 0; i < logIndentationLevel; i++) {
                    logPrefix += "\t";
                }
            }
            console.log(logPrefix + message);
        }
    };

    var startLogGroup = function (groupName) {
        logIndentationLevel++;
        if (typeof console !== "undefined" && typeof console.group !== "undefined") {
            console.group(groupName);
        } else {
            log(groupName);
        }
    };

    var endLogGroup = function () {
        if (logIndentationLevel > 0)
            logIndentationLevel--;
        if (typeof console !== "undefined" && typeof console.groupEnd !== "undefined") {
            console.groupEnd();
        }
    };

    return Eventing;
});
